import { Clip, Source } from './types'

export interface Channel {
  id: string
  title: string
  thumbnail?: string
}

export interface User {
  email: string
  name: string
  picture?: string
}

/** One call answers everything: who you are, and whether uploading is possible. */
export interface Me {
  configured: boolean
  domain: string
  issues: string[]
  user: User | null
  canManagePublisher: boolean
  publisher: {
    connected: boolean
    channel?: Channel | null
    connectedBy?: string | null
    error?: string
  }
}

export interface PublishJob {
  id: string
  stage: 'queued' | 'rendering' | 'uploading' | 'done' | 'error'
  progress: number
  message: string
  videoId?: string
  url?: string
  error?: string
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((body as { error?: string }).error || `Request failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

export const getMe = () => fetch('/api/me').then(json<Me>)
export const signOut = () => fetch('/api/auth/signout', { method: 'POST' }).then(json)
export const disconnectPublisher = () =>
  fetch('/api/publisher/disconnect', { method: 'POST' }).then(json)

/**
 * Google's consent screen refuses to run inside an iframe, so both sign-in and
 * the channel connection happen in a popup. The popup posts back when it lands;
 * we also watch for it closing, because popup blockers and stray clicks happen.
 */
export function openGooglePopup(path: string, signal: string, onDone: () => void) {
  const w = window.open(path, 'bloom-google', 'width=520,height=680')
  const onMsg = (e: MessageEvent) => {
    if (e.data === signal || e.data === 'bloom:auth-ok') finish()
  }
  const timer = window.setInterval(() => { if (w?.closed) finish() }, 800)
  function finish() {
    window.clearInterval(timer)
    window.removeEventListener('message', onMsg)
    onDone()
  }
  window.addEventListener('message', onMsg)
}

/**
 * Ships every take plus the edit list to the local server, which does the actual
 * cutting with ffmpeg and then uploads the result to YouTube. We send the raw
 * takes and let the server cut, so the browser never has to re-encode video.
 */
export async function publish(
  sources: Source[],
  clips: Clip[],
  meta: { title: string; description: string; renderOnly?: boolean },
  onUploadProgress?: (fraction: number) => void,
): Promise<{ jobId: string }> {
  const used = sources.filter((s) => clips.some((c) => c.sourceId === s.id))
  const index = new Map(used.map((s, i) => [s.id, i]))

  const form = new FormData()
  form.append(
    'meta',
    JSON.stringify({
      title: meta.title,
      description: meta.description,
      renderOnly: meta.renderOnly ?? false,
      clips: clips.map((c) => ({ source: index.get(c.sourceId), in: c.in, out: c.out })),
    }),
  )
  used.forEach((s, i) => form.append(`source_${i}`, s.blob, `take_${i}.webm`))

  // XHR rather than fetch: it's still the only way to get upload progress events.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/publish')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onUploadProgress?.(e.loaded / e.total)
    }
    xhr.onload = () => {
      try {
        const body = JSON.parse(xhr.responseText)
        if (xhr.status >= 200 && xhr.status < 300) resolve(body)
        else reject(new Error(body.error || `Upload failed (${xhr.status})`))
      } catch {
        reject(new Error('The server sent back an unreadable response.'))
      }
    }
    xhr.onerror = () => reject(new Error('Could not reach the Bloom server. Is it running?'))
    xhr.send(form)
  })
}

export const getJob = (id: string) => fetch(`/api/publish/${id}`).then(json<PublishJob>)

/** Ask the server to render the edit and hand it back as a downloadable file. */
export function downloadUrl(jobId: string) {
  return `/api/publish/${jobId}/file`
}
